import React, { useState } from 'react';
import Button from '../../../../components/common/Button/Button';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import Icon from '../../../../components/common/Icon/Icon';
import PhotoFullscreenModal from '../../../../components/common/PhotoFullscreenModal/PhotoFullscreenModal';
import SectionTitle from '../../../../components/common/SectionTitle/SectionTitle';
import { PhotosFormData, PhotoFile } from '../../../../types/registration';
import type { StudentBodyPhotoAssessment } from '../../../../services/PhotoService';
import BodyPhotoHistoryPanel from '../../../../components/students/BodyPhotoHistoryPanel/BodyPhotoHistoryPanel';
import AddBodyPhotosModal, { PHOTO_POSITIONS } from './AddBodyPhotosModal';
import type { BodyPhotoAssessmentDateParts } from '../../../../utils/studentBodyPhotoAssessmentDateFields';
import { bodyPhotoAssessmentDatePartsToIso, formatBodyPhotoAssessmentMonthLabel } from '../../../../utils/studentBodyPhotoAssessmentDateFields';
import styles from '../StudentRegister.module.css';

interface PhotoGuidelinesStepProps {
  data: PhotosFormData;
  assessmentDateParts: BodyPhotoAssessmentDateParts;
  onAssessmentDatePartsChange: (parts: BodyPhotoAssessmentDateParts) => void;
  onPhotoChange: (position: keyof PhotosFormData, photo: PhotoFile | null) => void;
  onMarkStoredPhotoForRemoval?: (position: keyof PhotosFormData) => void;
  disabled?: boolean;
  isLoading?: boolean;
  titleAction?: React.ReactNode;
  /** Histórico de envios corporais (edit mode). */
  assessments?: StudentBodyPhotoAssessment[];
  showHistory?: boolean;
  hasPendingBodyPhotoChanges?: boolean;
  onSaveBodyPhotos?: () => Promise<void>;
}

const formatAssessmentDateLabel = (parts: BodyPhotoAssessmentDateParts): string | null => {
  const iso = bodyPhotoAssessmentDatePartsToIso(parts);
  if (!iso) {
    return null;
  }

  const month = Number.parseInt(parts.month, 10);
  const day = Number.parseInt(parts.day, 10);
  const year = parts.year;
  if (!Number.isInteger(month) || !Number.isInteger(day)) {
    return iso;
  }

  return `${day} de ${formatBodyPhotoAssessmentMonthLabel(month)} de ${year}`;
};

const PhotoGuidelinesStep: React.FC<PhotoGuidelinesStepProps> = ({
  data,
  assessmentDateParts,
  onAssessmentDatePartsChange,
  onPhotoChange,
  onMarkStoredPhotoForRemoval,
  disabled = false,
  isLoading = false,
  titleAction,
  assessments = [],
  showHistory = false,
  hasPendingBodyPhotoChanges = false,
  onSaveBodyPhotos,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [pendingRemoveKey, setPendingRemoveKey] = useState<keyof PhotosFormData | null>(null);
  const [fullscreenPreview, setFullscreenPreview] = useState<{ src: string; alt: string } | null>(null);

  const pendingUploads = PHOTO_POSITIONS.filter(({ key }) => Boolean(data[key]?.file));
  const pendingAssessmentDateLabel = formatAssessmentDateLabel(assessmentDateParts);
  const pendingRemoveLabel = pendingRemoveKey
    ? PHOTO_POSITIONS.find((item) => item.key === pendingRemoveKey)?.label
    : null;
  const pendingRemovePhoto = pendingRemoveKey ? data[pendingRemoveKey] : null;

  const handleConfirmRemove = () => {
    if (!pendingRemoveKey) {
      return;
    }

    const photo = data[pendingRemoveKey];
    if (photo?.stored) {
      onMarkStoredPhotoForRemoval?.(pendingRemoveKey);
    } else if (photo?.preview.startsWith('blob:')) {
      URL.revokeObjectURL(photo.preview);
    }

    onPhotoChange(pendingRemoveKey, null);
    setPendingRemoveKey(null);
  };

  return (
    <div className={styles.card}>
      <div className={`${styles.sectionHeaderRow} ${styles.sectionHeaderRowTight}`}>
        <SectionTitle as="h2" text="Fotos corporais" />
        <div className={styles.photoHeaderActions}>
          {titleAction}
          {!disabled && (
            <Button
              type="button"
              variant="primary"
              size="small"
              icon="add"
              onClick={() => setAddModalOpen(true)}
            >
              Adicionar
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <p className={styles.sectionDescription}>Carregando fotos da aluna...</p>
      )}

      {showHistory && (
        <div className={styles.bodyPhotoHistorySection}>
          <SectionTitle as="h3" text="Histórico de envios" />
          <BodyPhotoHistoryPanel
            assessments={assessments}
            isLoading={isLoading}
          />
        </div>
      )}

      {pendingUploads.length > 0 && (
        <div className={styles.pendingPhotosSection}>
          <p className={styles.sectionDescription}>
            {pendingUploads.length}{' '}
            {pendingUploads.length === 1 ? 'foto pronta' : 'fotos prontas'}
            {' '}para salvar
            {pendingAssessmentDateLabel ? ` (data: ${pendingAssessmentDateLabel})` : ''}.
          </p>
          <div className={styles.photoGrid}>
            {pendingUploads.map(({ key, label }) => (
              <div key={key} className={styles.photoCard}>
                <label className={styles.label}>{label}</label>
                <div className={styles.photoPreviewContainer}>
                  <img
                    src={data[key]!.preview}
                    alt={label}
                    className={styles.photoPreview}
                  />
                  <div className={styles.photoPreviewActions}>
                    <button
                      type="button"
                      className={styles.photoPreviewActionBtn}
                      onClick={() => setFullscreenPreview({
                        src: data[key]!.preview,
                        alt: label,
                      })}
                      title="Ver em tela cheia"
                      aria-label="Ver em tela cheia"
                    >
                      <Icon name="fullscreen" size={18} />
                    </button>
                    {!disabled && (
                      <button
                        type="button"
                        className={`${styles.photoPreviewActionBtn} ${styles.photoPreviewActionBtnDanger}`}
                        onClick={() => setPendingRemoveKey(key)}
                        title="Remover foto"
                        aria-label="Remover foto"
                      >
                        <Icon name="delete" size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddBodyPhotosModal
        open={addModalOpen}
        data={data}
        assessmentDateParts={assessmentDateParts}
        onAssessmentDatePartsChange={onAssessmentDatePartsChange}
        onPhotoChange={onPhotoChange}
        onMarkStoredPhotoForRemoval={onMarkStoredPhotoForRemoval}
        disabled={disabled}
        canSave={hasPendingBodyPhotoChanges}
        onSave={onSaveBodyPhotos}
        onClose={() => setAddModalOpen(false)}
      />

      <ConfirmModal
        open={Boolean(pendingRemoveKey)}
        title="Remover foto"
        message={
          pendingRemoveLabel
            ? pendingRemovePhoto?.stored
              ? `Deseja realmente remover a foto ${pendingRemoveLabel.toLowerCase()}? A remoção será confirmada ao salvar.`
              : `Deseja realmente remover a foto ${pendingRemoveLabel.toLowerCase()}?`
            : 'Deseja realmente remover esta foto?'
        }
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="warning"
        icon="delete"
        onConfirm={handleConfirmRemove}
        onCancel={() => setPendingRemoveKey(null)}
      />

      <PhotoFullscreenModal
        open={Boolean(fullscreenPreview)}
        src={fullscreenPreview?.src ?? ''}
        alt={fullscreenPreview?.alt}
        onClose={() => setFullscreenPreview(null)}
      />
    </div>
  );
};

export default PhotoGuidelinesStep;
